import React from 'react';
import { AlertCircleIcon, LockIcon } from 'lucide-react';
import type { PortalApiError } from '../../lib/portalHttp';
interface QueryStateProps {
  loading: boolean;
  error: PortalApiError | null;
  /** Rows of skeleton to show while loading. */
  rows?: number;
  children: React.ReactNode;
}

/** Consistent loading, not-found, forbidden and error states for every portal read. */
export function QueryState({
  loading,
  error,
  rows = 3,
  children
}: QueryStateProps) {
  if (loading) {
    return <div className="space-y-3" role="status" aria-label="Loading" data-id="element-1041" data-mp="1w0ttyukeek5v">
        {Array.from({
        length: rows
      }, (_, i) => <div key={i} className="h-16 animate-pulse rounded-md bg-panel/70" data-id="element-1042" data-mp="2a0uweegb9zng" />)}
      </div>;
  }
  if (error) {
    const restricted = error.code === 'FORBIDDEN' || error.code === 'NOT_FOUND';
    const Icon = restricted ? LockIcon : AlertCircleIcon;
    return <div role="alert" className="flex gap-3 rounded-md border border-line bg-panel p-5" data-id="element-1043" data-mp="3he6fkmydpau4">
        <Icon className={`mt-0.5 h-5 w-5 shrink-0 ${restricted ? 'text-muted' : 'text-[#FF8B7A]'}`} aria-hidden="true" data-id="element-1044" data-mp="1p543bcidm36a" />
        <div data-id="element-1045" data-mp="1u2vx6c2kzzd4">
          <p className="text-[15px] font-medium text-ink" data-id="element-1046" data-mp="1u2m42fm8pc37">
            {error.code === 'FORBIDDEN' ? 'Your role does not include this' : error.code === 'NOT_FOUND' ? 'Not available' : 'Something went wrong'}
          </p>
          <p className="mt-1 text-[14px] text-body" data-id="element-1047" data-mp="232j0j3evff8t">{error.message}</p>
        </div>
      </div>;
  }
  return <>{children}</>;
}
export function EmptyState({
  title,
  body,
  action
}: {
  title: string;
  body?: string;
  action?: React.ReactNode;
}) {
  return <div className="rounded-md border border-dashed border-line px-6 py-10 text-center" data-id="element-1048" data-mp="1fesnospby5jk">
      <p className="text-[15px] font-medium text-ink" data-id="element-1049" data-mp="1k6aongrcs64j">{title}</p>
      {body && <p className="mx-auto mt-1.5 max-w-sm text-[14px] text-muted" data-id="element-1050" data-mp="mqdeimsvqhg5">{body}</p>}
      {action && <div className="mt-5" data-id="element-1051" data-mp="moj1rvrrhvn5">{action}</div>}
    </div>;
}
export function InlineError({
  error
}: {
  error: PortalApiError | null;
}) {
  if (!error) return null;
  return <p role="alert" className="text-[14px] text-[#FF8B7A]" data-id="element-1052" data-mp="1uyt28mbx5gon">
      {error.message}
    </p>;
}